import {
  ArrayMaxSize,
  ArrayMinSize,
  IsBoolean,
  IsIn,
  IsInt,
  IsObject,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  MinLength,
  ValidateIf,
} from 'class-validator';
import {
  FILE_PRIORITIES,
  FILE_STAGES,
  PRIORITY_REASONS,
  type FilePriority,
  type FileStage,
  type FileStatus,
  type PriorityReason,
} from './files.dto';
import type { SiteVisitStageData } from './stage-data.dto';
import type { VisitDetail } from './visits.dto';

/**
 * Officer productivity — smart lists, bulk actions, snippets, property history
 * and visit prefill.
 *
 * Everything here is officer-only: served by gov-api, never by the public API.
 * The shapes are shared verbatim by the web portal and the mobile app so the
 * two cannot drift on what "My overdue" means or how a snippet is chosen.
 */

// ── Smart lists and saved searches ───────────────────────────────────────────

/** Every file status, for validating a saved filter. Mirrors `FileStatus`. */
export const FILE_STATUSES: FileStatus[] = [
  'active',
  'awaiting_external',
  'paused',
  'emergency',
  'closed',
];

/** Statutory-clock narrowing a filter can ask for. */
export type DeadlineFilter = 'overdue' | 'due_this_week';
export const DEADLINE_FILTERS: DeadlineFilter[] = ['overdue', 'due_this_week'];

/** How many days ahead "due this week" looks. Inclusive of today. */
export const DUE_SOON_DAYS = 7;

/** Whose files. Omitted means anyone's — the caller's whole reachable caseload. */
export type AssigneeFilter = 'me' | 'unassigned';
export const ASSIGNEE_FILTERS: AssigneeFilter[] = ['me', 'unassigned'];

/**
 * One saved (or built-in) filter over the caller's caseload.
 *
 * Every field narrows; an empty object is the whole reachable caseload, open
 * files only unless `includeClosed` says otherwise. The server applies the
 * caller's scope *before* any of this, so a filter can only ever narrow what the
 * caller could already see — a shared search names criteria, never files.
 */
export interface FileFilters {
  assignee?: AssigneeFilter;
  /** One specific officer's files. Ignored when `assignee` is set. */
  officerId?: string;
  stages?: FileStage[];
  statuses?: FileStatus[];
  priorities?: FilePriority[];
  /** Any-of: a file carrying at least one of these tags. */
  tags?: string[];
  /** Substring over file number, civic address and owner name. */
  q?: string;
  deadline?: DeadlineFilter;
  /** Notice drafted at Step 5 but not yet served on the owner. */
  awaitingService?: boolean;
  localGovernmentId?: string;
  /** Closed files are left out unless this is true. */
  includeClosed?: boolean;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const MAX_FILTER_QUERY_LENGTH = 200;

function pickList<T extends string>(value: unknown, allowed: readonly T[]): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = [...new Set(value.filter((v): v is T => allowed.includes(v as T)))];
  return out.length ? out : undefined;
}

/**
 * Keep only the recognised filter keys with well-formed values.
 *
 * The filter arrives as a free-form object (this repo does not use
 * `@ValidateNested` — reflect-metadata is not loaded in the frontend bundle), so
 * it is checked here, at the boundary. Unknown keys and malformed values are
 * dropped rather than refused: a saved search written by an older client must
 * still load after a criterion is retired.
 */
export function sanitizeFileFilters(input: unknown): FileFilters {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const bag = input as Record<string, unknown>;
  const out: FileFilters = {};
  if (ASSIGNEE_FILTERS.includes(bag['assignee'] as AssigneeFilter)) {
    out.assignee = bag['assignee'] as AssigneeFilter;
  }
  if (typeof bag['officerId'] === 'string' && UUID_PATTERN.test(bag['officerId'])) {
    out.officerId = bag['officerId'];
  }
  const stages = pickList(bag['stages'], FILE_STAGES);
  if (stages) out.stages = stages;
  const statuses = pickList(bag['statuses'], FILE_STATUSES);
  if (statuses) out.statuses = statuses;
  const priorities = pickList(bag['priorities'], FILE_PRIORITIES);
  if (priorities) out.priorities = priorities;
  if (Array.isArray(bag['tags'])) {
    const tags = [
      ...new Set(
        bag['tags']
          .map((tag) => (typeof tag === 'string' ? normalizeTag(tag) : null))
          .filter((tag): tag is string => !!tag),
      ),
    ].slice(0, MAX_TAGS_PER_FILE);
    if (tags.length) out.tags = tags;
  }
  if (typeof bag['q'] === 'string' && bag['q'].trim()) {
    out.q = bag['q'].trim().slice(0, MAX_FILTER_QUERY_LENGTH);
  }
  if (DEADLINE_FILTERS.includes(bag['deadline'] as DeadlineFilter)) {
    out.deadline = bag['deadline'] as DeadlineFilter;
  }
  if (bag['awaitingService'] === true) out.awaitingService = true;
  if (typeof bag['localGovernmentId'] === 'string' && UUID_PATTERN.test(bag['localGovernmentId'])) {
    out.localGovernmentId = bag['localGovernmentId'];
  }
  if (bag['includeClosed'] === true) out.includeClosed = true;
  return out;
}

/** True when the filter narrows nothing — the whole open caseload. */
export function isEmptyFilter(filters: FileFilters): boolean {
  return Object.keys(sanitizeFileFilters(filters)).length === 0;
}

/** The smart lists every officer has without saving anything. */
export type BuiltinSmartList = 'my_files' | 'my_overdue' | 'due_this_week' | 'awaiting_service';
export const BUILTIN_SMART_LISTS: BuiltinSmartList[] = [
  'my_files',
  'my_overdue',
  'due_this_week',
  'awaiting_service',
];

export const BUILTIN_SMART_LIST_FILTERS: Readonly<Record<BuiltinSmartList, FileFilters>> = {
  my_files: { assignee: 'me' },
  my_overdue: { assignee: 'me', deadline: 'overdue' },
  due_this_week: { deadline: 'due_this_week' },
  awaiting_service: { awaitingService: true },
};

/** Key a smart list travels under in the API and in URLs. */
export function builtinListKey(list: BuiltinSmartList): string {
  return `builtin:${list}`;
}

export function savedListKey(id: string): string {
  return `saved:${id}`;
}

export type SmartListKey =
  { kind: 'builtin'; builtin: BuiltinSmartList } | { kind: 'saved'; id: string };

export function parseSmartListKey(key: string | null | undefined): SmartListKey | null {
  if (!key) return null;
  const [kind, rest] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
  if (kind === 'builtin' && BUILTIN_SMART_LISTS.includes(rest as BuiltinSmartList)) {
    return { kind: 'builtin', builtin: rest as BuiltinSmartList };
  }
  if (kind === 'saved' && UUID_PATTERN.test(rest)) return { kind: 'saved', id: rest };
  return null;
}

export const MAX_SAVED_SEARCH_NAME = 80;
/** Per user. A list of fifty saved searches is a list nobody scans. */
export const MAX_SAVED_SEARCHES_PER_USER = 50;

export interface SavedSearch {
  id: string;
  name: string;
  filters: FileFilters;
  /** Visible to everyone who can read files at `localGovernmentId`. */
  shared: boolean;
  localGovernmentId: string | null;
  ownerId: string;
  ownerName: string | null;
  /** The caller owns it — and so may rename, re-share or delete it. */
  mine: boolean;
  createdAt: string;
  updatedAt: string;
}

/** `POST /saved-searches`. `filters` is sanitised server-side. */
export class CreateSavedSearchDto {
  @IsString()
  @MinLength(1)
  @MaxLength(MAX_SAVED_SEARCH_NAME)
  name!: string;

  @IsObject()
  filters!: Record<string, unknown>;

  @IsOptional()
  @IsBoolean()
  shared?: boolean;

  /** Required when `shared` — a team is one municipality's officers. */
  @ValidateIf((o: CreateSavedSearchDto) => o.shared === true || o.localGovernmentId != null)
  @IsUUID()
  localGovernmentId?: string | null;
}

export class UpdateSavedSearchDto {
  @IsOptional()
  @IsString()
  @MinLength(1)
  @MaxLength(MAX_SAVED_SEARCH_NAME)
  name?: string;

  @IsOptional()
  @IsObject()
  filters?: Record<string, unknown>;

  @IsOptional()
  @IsBoolean()
  shared?: boolean;

  @IsOptional()
  @IsUUID()
  localGovernmentId?: string | null;
}

/** One row of the dashboard's smart-list strip. */
export interface SmartListCount {
  /** `builtin:<name>` or `saved:<uuid>` — see {@link parseSmartListKey}. */
  key: string;
  kind: 'builtin' | 'saved';
  builtin: BuiltinSmartList | null;
  savedSearch: SavedSearch | null;
  count: number;
}

export interface SmartListsResponse {
  lists: SmartListCount[];
}

/** `POST /files/query` — an ad-hoc filter, or a smart list by key. */
export class QueryFilesDto {
  @IsOptional()
  @IsObject()
  filters?: Record<string, unknown>;

  /** A smart list key; when present, `filters` is ignored. */
  @IsOptional()
  @IsString()
  @MaxLength(64)
  list?: string;

  @IsOptional()
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(1000)
  pageSize?: number;
}

// ── Tags ─────────────────────────────────────────────────────────────────────

export const MAX_TAG_LENGTH = 40;
export const MAX_TAGS_PER_FILE = 20;

/**
 * Canonical tag form: trimmed, inner whitespace collapsed, lower-cased, letters
 * (any script, so French accents survive), digits, space, `-` and `_` only.
 * Returns null for a tag with nothing left.
 */
export function normalizeTag(raw: string): string | null {
  const tag = raw
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('en-CA')
    .replace(/[^\p{L}\p{N} _-]/gu, '')
    .trim()
    .slice(0, MAX_TAG_LENGTH)
    .trim();
  return tag.length ? tag : null;
}

// ── Bulk actions ─────────────────────────────────────────────────────────────

export type BulkFileAction = 'reassign' | 'set_priority' | 'add_tag' | 'remove_tag';
export const BULK_FILE_ACTIONS: BulkFileAction[] = [
  'reassign',
  'set_priority',
  'add_tag',
  'remove_tag',
];

/** One request touches at most this many files. */
export const MAX_BULK_FILES = 200;

export class BulkFileActionDto {
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_BULK_FILES)
  @IsUUID(undefined, { each: true })
  fileIds!: string[];

  @IsIn(BULK_FILE_ACTIONS)
  action!: BulkFileAction;

  /** `reassign`: the officer to hand the files to, or null to unassign. */
  @IsOptional()
  @IsUUID()
  officerId?: string | null;

  @IsOptional()
  @IsIn(FILE_PRIORITIES)
  priority?: FilePriority;

  @IsOptional()
  @IsIn(PRIORITY_REASONS)
  priorityReason?: PriorityReason;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  priorityReasonOther?: string;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_TAG_LENGTH * 2)
  tag?: string;
}

/**
 * Why one file in a batch was not changed. A batch is never all-or-nothing: an
 * officer selecting forty files should get thirty-eight done and be told which
 * two were refused, not have the whole batch thrown back over one.
 */
export type BulkFileError =
  'not_found' | 'forbidden' | 'invalid' | 'disabled' | 'unchanged' | 'failed';

export interface BulkFileResult {
  fileId: string;
  reference: string | null;
  ok: boolean;
  error?: BulkFileError;
  message?: string;
}

export interface BulkFileActionResponse {
  /** Stamped on every file's audit entry so one batch can be read back whole. */
  bulkId: string;
  action: BulkFileAction;
  succeeded: number;
  failed: number;
  results: BulkFileResult[];
}

/** `POST /files/bulk/export` — CSV of the selected files the caller may read. */
export class BulkExportDto {
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_BULK_FILES)
  @IsUUID(undefined, { each: true })
  fileIds!: string[];
}

/** Columns of the bulk CSV export, in order. Header labels are localized. */
export const FILE_EXPORT_COLUMNS = [
  'reference',
  'address',
  'stage',
  'status',
  'priority',
  'assignedOfficer',
  'ownerName',
  'pan',
  'pid',
  'tags',
  'createdAt',
  'updatedAt',
] as const;
export type FileExportColumn = (typeof FILE_EXPORT_COLUMNS)[number];

export const FILE_EXPORT_HEADERS: Readonly<
  Record<'en' | 'fr', Readonly<Record<FileExportColumn, string>>>
> = {
  en: {
    reference: 'File number',
    address: 'Civic address',
    stage: 'Stage',
    status: 'Status',
    priority: 'Priority',
    assignedOfficer: 'Assigned officer',
    ownerName: 'Owner',
    pan: 'PAN',
    pid: 'PID',
    tags: 'Tags',
    createdAt: 'Opened',
    updatedAt: 'Last activity',
  },
  fr: {
    reference: 'Numéro de dossier',
    address: 'Adresse municipale',
    stage: 'Étape',
    status: 'Statut',
    priority: 'Priorité',
    assignedOfficer: 'Agent assigné',
    ownerName: 'Propriétaire',
    pan: 'NCB',
    pid: 'NID',
    tags: 'Étiquettes',
    createdAt: 'Ouvert le',
    updatedAt: 'Dernière activité',
  },
};

// ── Note snippets ────────────────────────────────────────────────────────────

export type SnippetScope = 'personal' | 'team';
export const SNIPPET_SCOPES: SnippetScope[] = ['personal', 'team'];

/** Where a snippet is offered. `any` shows in both pickers. */
export type SnippetContext = 'note' | 'letter' | 'any';
export const SNIPPET_CONTEXTS: SnippetContext[] = ['note', 'letter', 'any'];

export const MAX_SNIPPET_TITLE = 80;
export const MAX_SNIPPET_BODY = 4000;

export interface NoteSnippet {
  id: string;
  scope: SnippetScope;
  /** The team a team snippet belongs to; null for personal ones. */
  localGovernmentId: string | null;
  title: string;
  bodyEn: string | null;
  bodyFr: string | null;
  context: SnippetContext;
  ownerId: string;
  ownerName: string | null;
  /** Whether the caller may edit or delete it. */
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export class CreateNoteSnippetDto {
  @IsString()
  @MinLength(1)
  @MaxLength(MAX_SNIPPET_TITLE)
  title!: string;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_SNIPPET_BODY)
  bodyEn?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_SNIPPET_BODY)
  bodyFr?: string | null;

  @IsOptional()
  @IsIn(SNIPPET_CONTEXTS)
  context?: SnippetContext;

  @IsOptional()
  @IsIn(SNIPPET_SCOPES)
  scope?: SnippetScope;

  @ValidateIf((o: CreateNoteSnippetDto) => o.scope === 'team')
  @IsUUID()
  localGovernmentId?: string | null;
}

export class UpdateNoteSnippetDto {
  @IsOptional()
  @IsString()
  @MinLength(1)
  @MaxLength(MAX_SNIPPET_TITLE)
  title?: string;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_SNIPPET_BODY)
  bodyEn?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(MAX_SNIPPET_BODY)
  bodyFr?: string | null;

  @IsOptional()
  @IsIn(SNIPPET_CONTEXTS)
  context?: SnippetContext;
}

/**
 * The text a snippet inserts in `language`, falling back to the other variant
 * when the preferred one was never written. Equal weight means either may be
 * the only one an officer has bothered with; inserting nothing would be worse.
 */
export function snippetBody(
  snippet: Pick<NoteSnippet, 'bodyEn' | 'bodyFr'>,
  language: 'en' | 'fr',
): string {
  const preferred = language === 'fr' ? snippet.bodyFr : snippet.bodyEn;
  const other = language === 'fr' ? snippet.bodyEn : snippet.bodyFr;
  return (preferred?.trim() ? preferred : other) ?? '';
}

/** True when the snippet offers itself in a picker for `context`. */
export function snippetFits(snippet: Pick<NoteSnippet, 'context'>, context: 'note' | 'letter') {
  return snippet.context === 'any' || snippet.context === context;
}

/**
 * Splice `insert` into `text` at the selection, padding with a space or a
 * newline where the join would otherwise run two words together. Returns the
 * new text and where the caret belongs after it.
 */
export function insertAtSelection(
  text: string,
  insert: string,
  selectionStart: number = text.length,
  selectionEnd: number = selectionStart,
): { text: string; caret: number } {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const before = text.slice(0, start);
  const after = text.slice(end);
  const multiline = insert.includes('\n');
  const lead = before.length === 0 || /\s$/.test(before) ? '' : multiline ? '\n' : ' ';
  const trail = after.length === 0 || /^\s/.test(after) ? '' : multiline ? '\n' : ' ';
  const joined = `${before}${lead}${insert}${trail}`;
  return { text: `${joined}${after}`, caret: joined.length };
}

// ── Repeat last visit ────────────────────────────────────────────────────────

/** The parts of a site inspection worth carrying into a follow-up visit. */
export const REPEATABLE_SITE_VISIT_FIELDS = [
  'observations',
  'substantiated',
  'accessRefused',
  'assessment131_1',
  'assessment131_2',
  'assessment131_3',
  'assessment139',
  'informalNotice',
  'estimatedCost',
  'notes',
] as const satisfies readonly (keyof SiteVisitStageData)[];

export interface RepeatVisitPrefill {
  /** The completed visit the notes came from, if any. */
  sourceVisitId: string | null;
  /** When that visit (or the inspection) happened — shown so the officer knows how old it is. */
  sourceDate: string | null;
  /** Notes to start the follow-up visit's check-in with. */
  notes: string | null;
  /**
   * Inspection fields to start a re-inspection from. Dates are never carried:
   * the offense and inspection dates of a follow-up are facts of the new visit.
   */
  siteVisit: Partial<SiteVisitStageData>;
}

/**
 * Build the "repeat last visit" prefill for a follow-up.
 *
 * Pure, and shared by web and mobile so the two offer the same starting point.
 * Returns null when there is nothing to repeat — no completed visit with notes
 * and no recorded inspection — so the affordance can hide rather than offer an
 * empty form.
 */
export function buildRepeatVisitPrefill(
  visits: readonly VisitDetail[] | null | undefined,
  siteVisit: SiteVisitStageData | null | undefined,
): RepeatVisitPrefill | null {
  const completed = [...(visits ?? [])]
    .filter((v) => v.status === 'completed')
    .sort((a, b) => (b.completed?.at ?? b.updatedAt).localeCompare(a.completed?.at ?? a.updatedAt));
  const last = completed.find((v) => v.notes?.trim()) ?? completed[0] ?? null;

  const carried: Partial<SiteVisitStageData> = {};
  if (siteVisit) {
    for (const key of REPEATABLE_SITE_VISIT_FIELDS) {
      const value = siteVisit[key];
      if (value === undefined || value === null || value === '') continue;
      (carried as Record<string, unknown>)[key] = value;
    }
  }

  const notes = last?.notes?.trim() ? last.notes.trim() : null;
  if (!notes && Object.keys(carried).length === 0) return null;
  return {
    sourceVisitId: last?.id ?? null,
    sourceDate: last?.completed?.at ?? siteVisit?.inspectionDate ?? last?.updatedAt ?? null,
    notes,
    siteVisit: carried,
  };
}

// ── Property history ─────────────────────────────────────────────────────────

/** Why a file turned up in the history panel. */
export type PropertyMatch = 'pid' | 'pan' | 'address' | 'owner';

export interface PropertyHistoryFile {
  id: string;
  reference: string;
  address: string;
  stage: FileStage;
  status: FileStatus;
  open: boolean;
  createdAt: string;
  updatedAt: string;
  localGovernmentId: string | null;
  match: PropertyMatch[];
  /**
   * The caller may see this file only as the public does (a limited-licence
   * seat, or `file:public_read` alone): the owner is withheld and the row does
   * not link into the record.
   */
  redacted: boolean;
  ownerName: string | null;
}

export interface PropertyHistory {
  fileId: string;
  /** Prior and open files at the same PID, PAN or civic address. */
  sameProperty: PropertyHistoryFile[];
  /** Files at other properties of the same owner. Never redacted rows. */
  ownerProperties: PropertyHistoryFile[];
}

/**
 * Comparable form of a civic address: case, accents, punctuation and runs of
 * whitespace removed, and the common street-type spellings folded, so
 * "142 Main St." and "142 MAIN STREET" are the same property.
 */
export function normalizeAddress(address: string | null | undefined): string {
  if (!address) return '';
  const folded = address
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words: Record<string, string> = {
    street: 'st',
    avenue: 'ave',
    av: 'ave',
    road: 'rd',
    drive: 'dr',
    boulevard: 'blvd',
    // cspell:disable-next-line
    boul: 'blvd',
    chemin: 'ch',
    rue: 'rue',
    route: 'rte',
    court: 'crt',
    lane: 'ln',
    place: 'pl',
  };
  return folded
    .split(' ')
    .map((w) => words[w] ?? w)
    .join(' ');
}

// ── Per-municipality switch ──────────────────────────────────────────────────

/** `GET /productivity/settings` — where the data-touching tools are switched on. */
export interface ProductivitySettings {
  /** Municipalities in the caller's reach that have the tools on. */
  enabledLocalGovernmentIds: string[];
  /** True when the caller holds a platform-wide grant (every municipality). */
  all: boolean;
}

/**
 * Whether bulk actions and team sharing are on for a file at
 * `localGovernmentId`. A file with no municipality has nobody to switch it off
 * and follows the platform default (on).
 */
export function productivityEnabledFor(
  settings: ProductivitySettings | null | undefined,
  localGovernmentId: string | null | undefined,
): boolean {
  if (!settings) return false;
  if (!localGovernmentId) return true;
  return settings.enabledLocalGovernmentIds.includes(localGovernmentId);
}

// ── Recent files (client-side) ───────────────────────────────────────────────

export interface RecentFileEntry {
  id: string;
  reference: string;
  address: string;
  viewedAt: string;
}

export const MAX_RECENT_FILES = 8;

/** Put `entry` at the front of the list, de-duplicated by id and capped. */
export function pushRecentFile(
  list: readonly RecentFileEntry[] | null | undefined,
  entry: RecentFileEntry,
  max: number = MAX_RECENT_FILES,
): RecentFileEntry[] {
  return [entry, ...(list ?? []).filter((e) => e.id !== entry.id)].slice(0, max);
}
